import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import sortBy from 'lodash/sortBy';
import { getDefaultState } from '@/services/book/states';
import { ExerciseConverted, ExerciseResponse } from '@/interfaces/class/exercises';
import { Lesson } from '@/interfaces/class/lesson';
import { Training } from '@/interfaces/class/training';
import { BookState } from '@/interfaces/class/state';

const findNextExercise = (
  current: ExerciseConverted,
  list: {
    [key: string]: {
      [key: string]: ExerciseResponse[];
    };
  },
) => {
  let nextExercise = { id: 0, title: '' };

  const requiredKey = current?.required ? 'required' : 'optional';
  const activity = current?.activity === 'class' ? 'lesson' : 'homework';
  const currentList = list[activity][requiredKey];
  const currentIndex = findIndex(currentList, (el: { id: number; }) => el.id === current?.id);

  if (current?.activity === 'home') {
    if (currentIndex === list.homework[requiredKey].length - 1) {
      if (current?.required && list.homework.optional[0]) {
        nextExercise = {
          id: list.homework.optional[0].id,
          title: list.homework.optional[0].title,
        };
      } else {
        nextExercise = { id: list.lesson.required[0].id, title: 'Finish homework' };
      }
    } else {
      nextExercise = {
        id: list.homework[requiredKey][currentIndex + 1].id,
        title: list.homework[requiredKey][currentIndex + 1].title,
      };
    }
  } else {
    const summary: ExerciseResponse | {
      id: number;
      title: string;
    } = find(list.lesson.required, (el: ExerciseResponse) => el.alias === 'summary') || {
      id: 0,
      title: '',
    };

    if (!current?.required || currentIndex === list.lesson.required.length - 2) {
      if (summary.id) {
        nextExercise = { id: summary.id, title: summary.title };
      } else {
        nextExercise = {
          id: list.lesson?.required?.[0]?.id || 0,
          title: list.lesson?.required?.[0]?.title || '',
        };
      }
    } else if (currentIndex === list.lesson.required.length - 1) {
      nextExercise = {
        id: list.homework.required[0].id,
        title: list.homework.required[0].title,
      };
    } else {
      nextExercise = {
        id: list.lesson.required[currentIndex + 1].id,
        title: list.lesson.required[currentIndex + 1].title,
      };
    }
  }

  return { ...(current || {}), nextExercise };
};

const groupExercises = (
  exerciseId: string,
  exercises: ExerciseResponse[],
  trainings: Training[],
) => {
  let currentExercise : ExerciseConverted;
  const homeExercises: {
    [key: string]: ExerciseResponse[];
  } = {
    required: [],
    optional: [],
  };
  const classExercises: {
    [key: string]: ExerciseResponse[];
  } = {
    required: [],
    optional: [],
  };

  (exercises || []).forEach((exercise : ExerciseResponse) => {
    if (exercise.id === parseInt(exerciseId, 10)) {
      const elements = trainings.filter((el: Training) => !el.allowedTrainingTypes.length);

      currentExercise = getDefaultState({
        ...currentExercise,
        ...exercise,
        data: trainings,
        dataTraining: elements,
      });
    }

    const requiredKey = exercise.required ? 'required' : 'optional';

    if (exercise.activity === 'class') {
      classExercises[requiredKey].push(exercise);
    } else {
      homeExercises[requiredKey].push(exercise);
    }
  });

  ['required', 'optional'].forEach((el: string) => {
    const compare = (data: any) => sortBy(data, (training: {
      priority: number;
    }) => training.priority);

    homeExercises[el] = compare(homeExercises[el]);
    classExercises[el] = compare(classExercises[el]);
  });

  return {
    classExercises,
    currentExercise,
    homeExercises,
  };
};

const setClassStartState = (
  response: {
    data: {
      getExercises: ExerciseResponse[];
      getTrainingElements: Training[];
      getLessons: Lesson[];
    };
  },
  params: {
    courseId: string;
    exerciseId: string;
    lessonId: string;
  },
) => {
  const groupped = groupExercises(
    params.exerciseId,
    response.data.getExercises,
    response.data.getTrainingElements,
  );

  const currentExercise = findNextExercise(groupped.currentExercise, {
    lesson: groupped.classExercises,
    homework: groupped.homeExercises,
  });

  return {
    exercise: {
      currentExercise,
      exercises: {
        [params.lessonId]: {
          lesson: groupped.classExercises,
          homework: groupped.homeExercises,
        },
      },
    },
    lesson: {
      currentId: params.lessonId,
      lessons: {
        [params.courseId]: response.data.getLessons,
      },
    },
    vocabulary: {},
    sync: {
      activeTab: currentExercise.activity === 'home' ? 'homework' : 'lesson',
    },
  };
};

const setClassChangeState = (
  response: {
    data: {
      getExercises: ExerciseResponse[];
      getTrainingElements: Training[];
    };
  },
  params: {
    exerciseId: string;
    lessonId: string;
  },
  state: BookState,
) => {
  const changedState: BookState = state;

  const groupped = groupExercises(
    params.exerciseId,
    response.data.getExercises,
    response.data.getTrainingElements,
  );

  const currentExercise = findNextExercise(groupped.currentExercise, {
    lesson: groupped.classExercises,
    homework: groupped.homeExercises,
  });

  changedState.exercise = {
    // @ts-ignore
    currentExercise,
    // @ts-ignore
    exercises: {
      [params.lessonId]: {
        // @ts-ignore
        lesson: groupped.classExercises,
        homework: groupped.homeExercises,
      },
    },
  };

  changedState.route = { ...state.route, ...params };
  changedState.sync = {
    ...state.sync,
    activeTab: currentExercise.activity === 'home' ? 'homework' : 'lesson',
  };

  return changedState;
};

export {
  findNextExercise,
  groupExercises,
  setClassStartState,
  setClassChangeState,
};
